import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WorkOrder } from '@ups/xplat/api/dto';
import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  environment,
} from '@ups/xplat/core';

const GET_WORK_ORDERS_TO_COPY =
  '/api/timekeeping/job/{:jobID}/work-orders-to-copy';
const GET_JOBS_BY_CUSTOMER_PO = '/api/timekeeping/jobs-by-customer-po';

/** One job carrying a searched customer PO (WI 36820 AC 10), as the server sends it. */
export interface JobByCustomerPo {
  /** Sparta job id (Guid). */
  id: string;
  /** Viewpoint job number. */
  jobID: string;
  name: string;
  isActive: boolean;
  companyID: number | null;
  /** The distinct POs on this job that matched the search. */
  matchingPOs: string[];
}

/**
 * WI 36820: reads a SOURCE job's work orders for the Create Work Order dialog's "Copy from another job". Its own
 * service (not `TimekeepingService`) so that already oversized file does not grow.
 */
@Injectable({ providedIn: 'root' })
export class WorkOrderCopyService {
  private readonly spartaClient: HttpClient;

  constructor(clientFactory: MyHttpClientFactory) {
    this.spartaClient = clientFactory.createHttpClient(
      environment.urls.spartaAPI,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  /**
   * The active work orders of the source job. The server answers 403 when the caller holds no grant on that job (and
   * 404 for another company's job when the caller is company-scoped), so a refusal rejects: it is never an empty list.
   */
  getWorkOrdersToCopy(jobID: string): Promise<WorkOrder[]> {
    const url = GET_WORK_ORDERS_TO_COPY.split('{:jobID}').join(jobID);
    return this.spartaClient.get(url).toPromise() as Promise<WorkOrder[]>;
  }

  /**
   * The jobs (open and closed) whose contract lines carry a customer PO containing `po`. The server returns only jobs
   * the caller can already read, and nothing for fewer than 2 characters.
   */
  getJobsByCustomerPO(po: string): Promise<JobByCustomerPo[]> {
    const url = `${GET_JOBS_BY_CUSTOMER_PO}?po=${encodeURIComponent(po)}`;
    return this.spartaClient.get(url).toPromise() as Promise<JobByCustomerPo[]>;
  }
}
